import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { initials } from '../../lib/format';

export function Card({ children, className, as: As = 'section' }: { children: ReactNode; className?: string; as?: 'section' | 'div' | 'article' }) {
  return (
    <As className={cn('rounded-card border border-line bg-surface shadow-card', className)}>
      {children}
    </As>
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4', className)}>
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-ink-muted">{description}</p>}
      </div>
      {action && <div className="-my-2 flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  meta,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {back && <div className="mb-2">{back}</div>}
        <h1 className="text-2xl font-semibold tracking-tight text-ink sm:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1 text-[15px] text-ink-muted">{subtitle}</p>}
        {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const avatarPalette = ['#2F6B4F', '#1D5FA8', '#A15C00', '#7B3F99', '#B42318', '#0F766E', '#6B4F2F'];
function hashColour(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return avatarPalette[Math.abs(h) % avatarPalette.length]!;
}

export function Avatar({
  name,
  src,
  size = 40,
  className,
  square,
}: {
  name: string;
  src?: string | null | undefined;
  size?: number;
  className?: string;
  square?: boolean;
}) {
  const style = { width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.38)) };
  const shape = square ? 'rounded-[28%]' : 'rounded-full';
  if (src) {
    return <img src={src} alt="" loading="lazy" style={style} className={cn('shrink-0 bg-canvas object-cover', shape, className)} />;
  }
  return (
    <span
      aria-hidden="true"
      style={{ ...style, background: hashColour(name) }}
      className={cn('inline-flex shrink-0 items-center justify-center font-semibold text-white', shape, className)}
    >
      {initials(name)}
    </span>
  );
}

export function Stat({
  label,
  value,
  hint,
  icon,
  tone,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: 'ok' | 'warn' | 'danger' | 'info';
  className?: string;
}) {
  return (
    <Card as="div" className={cn('flex flex-col gap-1 p-4', className)}>
      <div className="flex items-center justify-between gap-2 text-sm text-ink-muted">
        <span>{label}</span>
        {icon && (
          <span
            className={cn(
              'flex size-8 items-center justify-center rounded-lg',
              tone === 'ok' && 'bg-ok-tint text-ok',
              tone === 'warn' && 'bg-warn-tint text-warn',
              tone === 'danger' && 'bg-danger-tint text-danger',
              (tone === 'info' || !tone) && 'bg-brand-tint text-brand',
            )}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
      </div>
      <div className="tabular text-[28px] leading-tight font-semibold tracking-tight text-ink">{value}</div>
      {hint && <div className="text-[13px] text-ink-muted">{hint}</div>}
    </Card>
  );
}

/** Two-column label/value list that stacks on phones. */
export function DescriptionList({ items, className }: { items: { label: ReactNode; value: ReactNode }[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[minmax(96px,38%)_1fr]', className)}>
      {items.map((it, i) => (
        <div key={i} className="contents">
          <dt className="text-sm text-ink-muted">{it.label}</dt>
          <dd className="-mt-2 text-[15px] break-words text-ink sm:mt-0">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Horizontal-scrolling table container so the page itself never scrolls sideways. */
export function TableWrap({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('-mx-px overflow-x-auto', className)}>{children}</div>;
}

export const th = 'px-4 py-2.5 text-left text-[12.5px] font-medium tracking-wide text-ink-muted uppercase whitespace-nowrap';
export const td = 'px-4 py-3 align-middle text-[15px]';
export const tr = 'border-t border-line';
